/**
 * The voice of a subject between the API and its screens: the open voice leads of the page's
 * subject and of a row of *Subjekty* — the page's own mapper (`mappers/subject-page`) ends with
 * these two — and what a read would say of the person's voiceprints (the lines the voice card
 * gains). The voiceprints are not served yet, so `toSubjectVoice` is called by stories and tests
 * alone.
 */
import type { SubjectPageData } from '@/components/humint/subject/subject-types'
import type { SubjectSummary } from '@/components/humint/types'
import type { SubjectVoice } from '@/components/humint/voice/card/voice-card-types'
import type {
  SubjectListVoiceFields,
  SubjectPageVoiceFields,
} from '@/services/api/humint-voice-matching'
import { toSubjectVoiceLeads, toVoiceprints } from './voiceprint-mappers'

/**
 * What a read says of the person's voice, for the host that shows it: the voiceprints, and the
 * comparison of the first open lead. A read without `voiceprints` (it names the leads only)
 * carries none, and the voice card stays on the samples alone.
 */
export function toSubjectVoice(fields: SubjectPageVoiceFields): SubjectVoice {
  return {
    ...(fields.voiceprints && { voiceprints: toVoiceprints(fields.voiceprints) }),
    leadPairId: toSubjectVoiceLeads(fields.voice_leads).pairId,
  }
}

/**
 * The page with the open voice leads a read counts: they head the page as a pill, which opens
 * the comparison of the first one. A server that says nothing of the voice, or does not count
 * leads, leaves the page exactly as it was mapped.
 */
export function withSubjectVoiceLeads(
  page: SubjectPageData,
  fields: SubjectPageVoiceFields | null | undefined
): SubjectPageData {
  if (!fields?.voice_leads) return page
  const { count, pairId, voiceprintId } = toSubjectVoiceLeads(fields.voice_leads)
  return {
    ...page,
    subject: { ...page.subject, voiceLeads: count },
    voiceLead: pairId ? { pairId, voiceprintId } : null,
  }
}

/** A row of *Subjekty* with its open voice leads, when the read says them. */
export function withVoiceLeads(
  subject: SubjectSummary,
  item: SubjectListVoiceFields
): SubjectSummary {
  return typeof item.voice_leads === 'number'
    ? { ...subject, voiceLeads: Math.max(0, item.voice_leads) }
    : subject
}
